import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import "../styles/dashboard.css";
import "../styles/EmployeeDetailPage.css";
import CustomAlert from "./CustomAlert";
import { useUser } from "./Context/UserContext";

import api from '../components/axios'

// Total Experience and Previous Experience are hidden on this page for now.
// HIDDEN ONLY - the values are still fetched and still drive the experience
// calculations below. Flip this to true to show the two fields again.
// Mirrors the flag of the same name in Dashboard.js.
const SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE = false;

/* Experience reads as full words — "0 Months", never a clipped "0 mo". */
const expYears = n => `${n || 0} ${Number(n) === 1 ? "Year" : "Years"}`;
const expMonths = n => `${n || 0} ${Number(n) === 1 ? "Month" : "Months"}`;
const isNonTechnicalCategory = (category) =>
    String(category || "").replace(/[^a-z]/gi, "").toLowerCase() === "nontechnical";
export default function EmployeeDetailPage() {
    const { empId, userInfo } = useUser();
    const { employeeId } = useParams();
    const history = useHistory();

    // ✅ STATE VARIABLES
    const [alert, setAlert] = useState({ show: false, message: "", type: "success" });
    const [isManager, setIsManager] = useState(false);  // Is user the employee's manager?
    const [isFreezeWindowOpen, setIsFreezeWindowOpen] = useState(false);  // Can submit now?
    const [employeeData, setEmployeeData] = useState({});
    const [additionalDetails, setAdditionalDetails] = useState({});
    const [primarySkills, setPrimarySkills] = useState([]);
    const [secondarySkills, setSecondarySkills] = useState([]);
    const [totalExp, setTotalExp] = useState({ years: 0, months: 0 });
    const [mindteckExp, setMindteckExp] = useState({ years: 0, months: 0 });
    const [previousExp, setPreviousExp] = useState({ years: 0, months: 0 });
    const [step, setStep] = useState(1);
    const [roles, setRoles] = useState([]);
    const [domains, setDomains] = useState([]);
    const [serviceLines, setServiceLines] = useState([]);
    const [managerTraining, setManagerTraining] = useState("");
    const [trainingDuration, setTrainingDuration] = useState("");
    const [managerReview, setManagerReview] = useState(null);
    const [formData, setFormData] = useState({
        role: "",
        domain: "",
        serviceLine: "",
        location: "",
        trainingRequirement: "",
        trainingRequiredName: "",
    });
    const [isUpdate, setIsUpdate] = useState(false);
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // SM-MGR-010 — "Request corrections": the manager can route a submission
    // back to the employee instead of approving it.
    const [correctionRequested, setCorrectionRequested] = useState(false);
    const [correctionLoading, setCorrectionLoading] = useState(false);
    const [hasSubmission, setHasSubmission] = useState(false);

    // Bumped to re-read the employee's record and the cycle status, so a
    // resubmission made after a correction request shows up here.
    const [refreshKey, setRefreshKey] = useState(0);

    // ✅ FETCH MASTER DATA (ROLES, DOMAINS, SERVICE LINES)
    useEffect(() => {
        async function fetchData() {
            try {
                const [r, d, s] = await Promise.all([
                    axios.get("/api/roles"),
                    axios.get("/api/domains"),
                    axios.get("/api/serviceLines"),
                ]);
                setRoles(r.data);
                setDomains(d.data);
                setServiceLines(s.data);
            } catch (error) {
                console.error("Error fetching master data:", error);
            }
        }
        fetchData();
    }, []);

    // ✅ SINGLE SOURCE OF TRUTH for isFreezeWindowOpen / isSubmitted on this page.
    // Uses the consolidated /api/Unfreeze/submission-status/{empId} endpoint,
    // keyed on the EMPLOYEE BEING REVIEWED (employeeId from the route) —
    // NOT the logged-in manager's own empId. The window/override lookup is
    // per-employee, so checking it against the manager's id would silently
    // ignore any individual override set for the employee being reviewed.
    const fetchSubmissionStatus = async () => {
        if (!employeeId) return;

        try {
            const res = await api.get(
                `/api/Unfreeze/submission-status/${employeeId}`
            );
            const {
                isWindowOpen,
                managerSubmitted,
                correctionRequested: correction,
            } = res.data;

            setIsFreezeWindowOpen(isWindowOpen);
            setCorrectionRequested(Boolean(correction));

            // Locked if the window is closed OR the manager already
            // submitted a review for this employee in the current cycle.
            // A pending correction also locks the review form — the ball is
            // in the employee's court until they resubmit.
            setIsSubmitted(!isWindowOpen || managerSubmitted || Boolean(correction));
        } catch (error) {
            console.error("Error checking submission status:", error);
            setIsFreezeWindowOpen(false);
            setIsSubmitted(true);
        }
    };

    useEffect(() => {
        fetchSubmissionStatus();
    }, [employeeId, refreshKey]);

    // ✅ FETCH THE EMPLOYEE'S SKILL RECORD + ADDITIONAL DETAILS
    useEffect(() => {
        if (!employeeId || !empId) return;

        const fetchEmployeeData = async () => {
            // 1️⃣ Employee skill data
            try {
                const res = await api.get(`/api/EmployeeSkill/${employeeId}`);
                const data = res.data;

                if (data) {
                    setEmployeeData(data);
                    setHasSubmission(true);

                    // ✅ CHECK IF LOGGED-IN USER IS THE MANAGER
                    const userIsManager =
                        String(empId).trim() === String(data.reportingManagerId).trim();
                    setIsManager(userIsManager);

                    console.log("Manager status check:", {
                        loggedInUserId: empId,
                        employeeReportingManager: data.reportingManagerId,
                        isUserTheManager: userIsManager
                    });

                    setFormData({
                        certification: data.certification,
                        certificationName: data.certificationName,
                        trainingReceived: data.trainingReceived,
                        trainingName: data.trainingName,
                        role: data.roleId,
                        domain: data.domainId,
                        serviceLine: data.serviceLineId,
                        additionalSkill: data.additionalSkill
                    });

                    setPrimarySkills(
                        (data.primarySkills || []).map(skill => ({
                            skill: String(skill.id),
                            level: skill.competency,
                            name: skill.name
                        }))
                    );

                    setSecondarySkills(
                        (data.secondarySkills || []).map(skill => ({
                            skill: String(skill.id),
                            level: skill.competency,
                            name: skill.name
                        }))
                    );

                    if (data.domainId) {
                        const skillRes = await axios.get(
                            `/api/skills/by-domain/${data.domainId}`
                        );
                        setSkills(skillRes.data.map(s => ({ ...s, id: String(s.id) })));
                    }

                    setIsUpdate(true);

                    // ✅ isSubmitted is NOT set here — it is owned exclusively
                    // by the submission-status effect above, which asks the
                    // backend directly whether the current cycle is used up.
                }
            } catch (err) {
                if (err.response?.status === 401) {
                    history.push("/home");
                    return;
                }
                console.error("Error fetching employee skill data:", err);
            }

            // 2️⃣ Additional employee details
            try {
                const additionalRes = await api.get(
                    `/api/auth/additional-details/${employeeId}`
                );

                setAdditionalDetails(additionalRes.data || {});
            } catch (error) {
                console.error("Error fetching additional details:", error);
            }
        };

        fetchEmployeeData();
    }, [employeeId, empId, history, refreshKey]);

    /*
     * The employee may resubmit while the manager is sitting on this page — that
     * is exactly what "Request Corrections" asks them to do. Without this, the
     * manager keeps looking at the pre-correction submission until they navigate
     * away and back, which reads as "my correction did nothing".
     *
     * Re-reading when the tab regains focus covers the realistic case: the
     * manager switches away, the employee resubmits, the manager comes back.
     */
    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === "visible") {
                setRefreshKey(k => k + 1);
            }
        };

        window.addEventListener("focus", refresh);
        document.addEventListener("visibilitychange", refresh);

        return () => {
            window.removeEventListener("focus", refresh);
            document.removeEventListener("visibilitychange", refresh);
        };
    }, []);

    // ✅ FETCH PREVIOUS EXPERIENCE
    useEffect(() => {
        if (!employeeId) return;

        axios
            .get(`/api/EmployeeSkill/previous-experience/${employeeId}`)
            .then(res => {
                setPreviousExp({
                    years: res.data.years || 0,
                    months: res.data.months || 0
                });
            })
            .catch(() => {
                setPreviousExp({ years: 0, months: 0 });
            });
    }, [employeeId]);

    // ✅ CALCULATE MINDTECK EXPERIENCE FROM DOJ
    useEffect(() => {
        if (!employeeData?.doj) return;

        const dojDate = new Date(employeeData.doj);
        const today = new Date();

        let years = today.getFullYear() - dojDate.getFullYear();
        let months = today.getMonth() - dojDate.getMonth();
        let days = today.getDate() - dojDate.getDate();

        if (days < 0) {
            months--;
        }

        if (months < 0) {
            years--;
            months += 12;
        }

        setMindteckExp({
            years: years < 0 ? 0 : years,
            months: months < 0 ? 0 : months
        });
    }, [employeeData?.doj]);

    // ✅ CALCULATE TOTAL EXPERIENCE = PREVIOUS + MINDTECK
    useEffect(() => {
        const prevYears = parseInt(previousExp.years || 0);
        const prevMonths = parseInt(previousExp.months || 0);
        const mindYears = parseInt(mindteckExp.years || 0);
        const mindMonths = parseInt(mindteckExp.months || 0);

        let totalMonths = prevMonths + mindMonths;
        let totalYears = prevYears + mindYears;

        if (totalMonths >= 12) {
            totalYears += Math.floor(totalMonths / 12);
            totalMonths = totalMonths % 12;
        }

        setTotalExp({
            years: totalYears,
            months: totalMonths
        });
    }, [previousExp, mindteckExp]);

    // ✅ FETCH LATEST MANAGER REVIEW CONTENT — pre-fill only.
    // isSubmitted / isFreezeWindowOpen are owned exclusively by the
    // submission-status effect above; this function no longer touches
    // either of them, it just populates the review fields for display.
    const fetchLatestManagerReview = async (employeeIdForReview) => {
        if (!employeeIdForReview) return;

        try {
            const response = await api.get(
                `/api/EmployeeSkill/latest-review/${employeeIdForReview}?loggedInUserId=${empId}`
            );
            const review = response.data;

            if (!review) {
                setManagerReview(null);
                return;
            }

            setManagerReview(review);

            /*
             * Levels come back keyed by SKILL ID (review.levels), so each one is
             * matched to the skill it actually belongs to. Matching by position
             * was the old bug: after a correction the employee can change or
             * reorder their skills, and slot 1 is then a different skill, so the
             * manager's assessment landed on the wrong one.
             */
            const levelFor = (skillId, skillType) =>
                (review.levels || []).find(
                    l =>
                        String(l.skillId) === String(skillId) &&
                        (l.skillType || "Primary").toLowerCase() === skillType
                ) || {};

            const applyLevels = (list, skillType) =>
                list.map(s => {
                    const match = levelFor(s.skill, skillType);
                    return {
                        ...s,
                        managerLevel: match.managerLevel || "",
                        targetLevel: match.targetLevel || ""
                    };
                });

            setPrimarySkills(prev => applyLevels(prev, "primary"));
            setSecondarySkills(prev => applyLevels(prev, "secondary"));

            setManagerTraining(review.recommendedTraining || "");
            setTrainingDuration(review.trainingDuration || "");
        } catch (error) {
            console.error("Error in fetchLatestManagerReview:", error);
        }
    };

    useEffect(() => {
        if (employeeData?.employeeId) {
            fetchLatestManagerReview(employeeData.employeeId);
        }
    }, [employeeData]);

    // ✅ HELPER FUNCTIONS
    const getRoleName = id => {
        const role = roles.find(r => r.id === id);
        return role ? role.name : id;
    };

    const getDomainName = id => {
        const domain = domains.find(d => d.id === id);
        return domain ? domain.name : id;
    };

    const getServiceLineName = id => {
        const service = serviceLines.find(s => s.id === id);
        return service ? service.name : id;
    };

    // ✅ HANDLE MANAGER LEVEL CHANGE
    const handleManagerLevelChange = (index, value, type) => {
        if (type === "primary") {
            const updated = [...primarySkills];
            updated[index].managerLevel = value;
            setPrimarySkills(updated);
        } else {
            const updated = [...secondarySkills];
            updated[index].managerLevel = value;
            setSecondarySkills(updated);
        }
    };

    // ✅ HANDLE TARGET LEVEL CHANGE
    const handleTargetLevelChange = (index, value, type) => {
        if (type === "primary") {
            const updated = [...primarySkills];
            updated[index].targetLevel = value;
            setPrimarySkills(updated);
        } else {
            const updated = [...secondarySkills];
            updated[index].targetLevel = value;
            setSecondarySkills(updated);
        }
    };

    // ✅ HANDLE SAVE
    const handleSaveEmployeeSkills = async () => {
        if (isSubmitted) return;

        // Validation
        for (let s of primarySkills) {
            if (!s.managerLevel) {
                setAlert({
                    show: true,
                    message: "Please provide manager review for all primary skills.",
                    type: "error"
                });
                return;
            }
        }

        for (let s of secondarySkills) {
            if (!s.managerLevel) {
                setAlert({
                    show: true,
                    message: "Please provide manager review for all secondary skills.",
                    type: "error"
                });
                return;
            }
        }

        const payload = {
            EmployeeSkillId: employeeData.id,
            EmployeeId: employeeData.employeeId,
            EmployeeName: employeeData.employeeName,

            // Levels are sent keyed by SKILL ID, so the server stores each
            // assessment against the skill it belongs to rather than a slot.
            Levels: [
                ...primarySkills
                    .filter(s => s.skill)
                    .map(s => ({
                        SkillId: Number(s.skill),
                        SkillType: "Primary",
                        ManagerLevel: s.managerLevel || "",
                        TargetLevel: s.targetLevel || ""
                    })),
                ...secondarySkills
                    .filter(s => s.skill)
                    .map(s => ({
                        SkillId: Number(s.skill),
                        SkillType: "Secondary",
                        ManagerLevel: s.managerLevel || "",
                        TargetLevel: s.targetLevel || ""
                    }))
            ],

            RecommendedTraining: managerTraining || "",
            TrainingDuration: trainingDuration || "",

            ReviewedBy: employeeData.reportingManagerId || "",
            ReviewedOn: new Date(),
            IsSubmitted: 1
        };

        setLoading(true);

        try {
            const response = await api.post("/api/EmployeeSkill/save-review", payload);

            console.log("Response:", response.data);

            setAlert({
                show: true,
                message: "Employee skills saved successfully!",
                type: "success"
            });

            setIsSubmitted(true);
        } catch (error) {
            console.error("Error saving employee skills:", error);

            console.log("Status:", error.response?.status);
            console.log("Response:", error.response?.data);

            // ✅ 409 = backend already knows this cycle is used up (window
            // closed, or a review was already submitted for this employee
            // this cycle). Surface the server's message and re-lock the
            // button instead of falling into the generic error path.
            if (error.response?.status === 409) {
                setAlert({
                    show: true,
                    message: error.response?.data?.message || "Already submitted for this cycle.",
                    type: "error"
                });
                setIsSubmitted(true);
            } else {
                setAlert({
                    show: true,
                    message: error.response?.data?.message || "Error saving employee skills. Please try again.",
                    type: "error"
                });
            }
        } finally {
            setLoading(false);
        }
    };

 

    const goToStep = (nextStep) => {
        setStep(nextStep);
        window.scrollTo({ top: 0, behavior: "auto" });
    };

    // ✅ RENDER
    return (
        <div className="dashboard-container">
            <div className="form-card card-mt reveal">
                <div className="form-stepper" aria-hidden="true">
                    <span className={`stepper-dot ${step >= 1 ? "active" : ""}`}>1</span>
                    <span className={`stepper-line ${step >= 2 ? "active" : ""}`} />
                    <span className={`stepper-dot ${step >= 2 ? "active" : ""}`}>2</span>
                </div>

                <h2 className="form-title">
                    {step === 1
                        ? "Employee Details & Experience"
                        : "Skills, Certifications, Upskilling & Reskilling"}
                </h2>
                <p className="form-subtitle">
                    {step === 1
                        ? `Reviewing ${employeeData.employeeName || "employee"} — profile and experience.`
                        : `Reviewing ${employeeData.employeeName || "employee"} — skills, competency and training.`}
                </p>

                <div className="form-grid">
                    {/* ========== STEP 1 ========== */}
                    {step === 1 && (
                        <>
                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-employeeId">Employee ID</label>
                                <input
                                    id="empdetail-employeeId"
                                    value={employeeData.employeeId || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-employeeName">Employee Name</label>
                                <input
                                    id="empdetail-employeeName"
                                    value={employeeData.employeeName || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-businessType">Business Type</label>
                                <input
                                    id="empdetail-businessType"
                                    value={additionalDetails?.businessType || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-region">Region</label>
                                <input
                                    id="empdetail-region"
                                    value={additionalDetails?.region || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-subRegion">Sub Region</label>
                                <input
                                    id="empdetail-subRegion"
                                    value={additionalDetails?.subRegion || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-workLocation">Work Location</label>
                                <input
                                    id="empdetail-workLocation"
                                    value={additionalDetails?.workLocation || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-department">Department</label>
                                <input
                                    id="empdetail-department"
                                    value={additionalDetails?.department || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-reportingManagerId">Reporting Manager ID</label>
                                <input
                                    id="empdetail-reportingManagerId"
                                    value={employeeData.reportingManagerId || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-reportingManagerName">Reporting Manager Name</label>
                                <input
                                    id="empdetail-reportingManagerName"
                                    value={employeeData.reportingManagerName || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-employeeCategory">Employee Category</label>
                                <input
                                    id="empdetail-employeeCategory"
                                    value={additionalDetails.employeeCategory || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            <div className="form-group">
                                <label className="label-abadi" htmlFor="empdetail-designation">Employee Designation</label>
                                <input
                                    id="empdetail-designation"
                                    value={additionalDetails.jobTitle_Desc || ""}
                                    disabled
                                    className="readonly-field"
                                />
                            </div>

                            {/* Total Experience — hidden, see
                                SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE */}
                            {SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE && (
                            <fieldset className="form-group">
                                <legend className="label-abadi">Total Experience</legend>
                                <div className="experience-dropdowns">
                                    <label htmlFor="empdetail-totalExpYears" className="sr-only">
                                        Total Experience Years
                                    </label>
                                    <select id="empdetail-totalExpYears" value={totalExp.years || ""} disabled>
                                        <option>{expYears(totalExp.years)}</option>
                                    </select>

                                    <label htmlFor="empdetail-totalExpMonths" className="sr-only">
                                        Total Experience Months
                                    </label>
                                    <select id="empdetail-totalExpMonths" value={totalExp.months || ""} disabled>
                                        <option>{expMonths(totalExp.months)}</option>
                                    </select>
                                </div>
                            </fieldset>
                            )}

                            {/* Previous Experience — hidden, see
                                SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE */}
                            {SHOW_TOTAL_AND_PREVIOUS_EXPERIENCE && (
                            <fieldset className="form-group">
                                <legend className="label-abadi">Previous Experience</legend>
                                <div className="experience-dropdowns">
                                    <label htmlFor="empdetail-previousExpYears" className="sr-only">
                                        Previous Experience Years
                                    </label>
                                    <select id="empdetail-previousExpYears" value={previousExp.years || ""} disabled>
                                        <option>{expYears(previousExp.years)}</option>
                                    </select>

                                    <label htmlFor="empdetail-previousExpMonths" className="sr-only">
                                        Previous Experience Months
                                    </label>
                                    <select id="empdetail-previousExpMonths" value={previousExp.months || ""} disabled>
                                        <option>{expMonths(previousExp.months)}</option>
                                    </select>
                                </div>
                            </fieldset>
                            )}

                            <fieldset className="form-group">
                                <legend className="label-abadi">Mindteck Experience</legend>
                                <div className="experience-dropdowns">
                                    <label htmlFor="empdetail-mindteckExpYears" className="sr-only">
                                        Mindteck Experience Years
                                    </label>
                                    <select id="empdetail-mindteckExpYears" value={mindteckExp.years} disabled>
                                        <option value={mindteckExp.years}>
                                            {expYears(mindteckExp.years)}
                                        </option>
                                    </select>

                                    <label htmlFor="empdetail-mindteckExpMonths" className="sr-only">
                                        Mindteck Experience Months
                                    </label>
                                    <select id="empdetail-mindteckExpMonths" value={mindteckExp.months} disabled>
                                        <option value={mindteckExp.months}>
                                            {expMonths(mindteckExp.months)}
                                        </option>
                                    </select>
                                </div>
                            </fieldset>

                            <div className="form-group full-width button-row">
                                <button
                                    type="button"
                                    className="submit-btn small-btn next-btn"
                                    onClick={() => goToStep(2)}
                                >
                                    Next →
                                </button>
                            </div>
                        </>
                    )}

                    {/* ========== STEP 2 ========== */}
                    {step === 2 && (
                        <>
                            {/* DOMAIN — Technical employees only. Non-Technical
                                employees sit under Service Line → Role → Skill,
                                with no Domain above it. */}
                            {!isNonTechnicalCategory(additionalDetails?.employeeCategory) && (
                                    <div className="form-group">
                                        <label
                                            className="label-abadi"
                                            htmlFor="empdetail-domain"
                                        >
                                            Domain
                                        </label>
                                        <input
                                            id="empdetail-domain"
                                            value={getDomainName(employeeData.domainId)}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                            )}

                            {/* SERVICE LINE / ROLE — both categories */}
                            <>
                                    <div className="form-group">
                                        <label
                                            className="label-abadi"
                                            htmlFor="empdetail-serviceLine"
                                        >
                                            Service Line
                                        </label>
                                        <input
                                            id="empdetail-serviceLine"
                                            value={getServiceLineName(employeeData.serviceLineId)}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label
                                            className="label-abadi"
                                            htmlFor="empdetail-role"
                                        >
                                            Role
                                        </label>
                                        <input
                                            id="empdetail-role"
                                            value={getRoleName(employeeData.roleId)}
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                            </>

                            {/* ================= SKILLS TABLE ================= */}
                            <div className="skills-section full-width">

                                <div className="review-skill-row skill-header">
                                    <div>Skill</div>
                                    <div>Self Competency Level</div>
                                    <div>Manager Evaluated Competency Level</div>
                                    <div>Target Competency Level</div>
                                </div>

                                {/* ================= PRIMARY SKILLS ================= */}
                                <div className="skill-section">
                                    <h3>Primary Skills</h3>

                                    {primarySkills.length === 0 ? (
                                        <span>No Primary Skills</span>
                                    ) : (
                                        primarySkills.map((s, i) => (
                                            <div
                                                key={`primary-${i}`}
                                                className="review-skill-row"
                                            >
                                                {/* Skill Name */}
                                                <input
                                                    value={s.name || ""}
                                                    disabled
                                                    className="readonly-field"
                                                    aria-label={`Primary Skill ${i + 1} name`}
                                                />

                                                {/* Self Competency */}
                                                <select
                                                    value={s.level || ""}
                                                    disabled
                                                    className={`competency-dropdown ${s.level?.toLowerCase() || ""
                                                        }`}
                                                    aria-label={`Primary Skill ${i + 1
                                                        } self competency level`}
                                                >
                                                    <option value="">
                                                        {s.level || "Not Provided"}
                                                    </option>
                                                </select>

                                                {/* Manager Competency */}
                                                <select
                                                    value={s.managerLevel || ""}
                                                    onChange={e =>
                                                        handleManagerLevelChange(
                                                            i,
                                                            e.target.value,
                                                            "primary"
                                                        )
                                                    }
                                                    className={`manager-review-dropdown ${s.managerLevel?.toLowerCase() || ""
                                                        }`}
                                                    required
                                                    aria-label={`Primary Skill ${i + 1
                                                        } manager evaluated competency level`}
                                                    disabled={
                                                        isSubmitted ||
                                                        !isFreezeWindowOpen ||
                                                        !isManager
                                                    }
                                                >
                                                    <option value="">
                                                        Manager Review
                                                    </option>
                                                    <option value="Beginner">
                                                        Beginner
                                                    </option>
                                                    <option value="Intermediate">
                                                        Intermediate
                                                    </option>
                                                    <option value="Expert">
                                                        Expert
                                                    </option>
                                                </select>

                                                {/* Target Competency */}
                                                <select
                                                    value={s.targetLevel || ""}
                                                    onChange={e =>
                                                        handleTargetLevelChange(
                                                            i,
                                                            e.target.value,
                                                            "primary"
                                                        )
                                                    }
                                                    className={`manager-review-dropdown ${s.targetLevel?.toLowerCase() || ""
                                                        }`}
                                                    aria-label={`Primary Skill ${i + 1
                                                        } target competency level`}
                                                    disabled={
                                                        isSubmitted ||
                                                        !isFreezeWindowOpen ||
                                                        !isManager
                                                    }
                                                >
                                                    <option value="">
                                                        Target Level
                                                    </option>
                                                    <option value="Beginner">
                                                        Beginner
                                                    </option>
                                                    <option value="Intermediate">
                                                        Intermediate
                                                    </option>
                                                    <option value="Expert">
                                                        Expert
                                                    </option>
                                                </select>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* ================= SECONDARY SKILLS ================= */}
                                <div className="skill-section">
                                    <h3>Secondary Skills</h3>

                                    {secondarySkills.length === 0 ? (
                                        <span>No Secondary Skills</span>
                                    ) : (
                                        secondarySkills.map((s, i) => (
                                            <div
                                                key={`secondary-${i}`}
                                                className="review-skill-row"
                                            >
                                                {/* Skill Name */}
                                                <input
                                                    value={s.name || ""}
                                                    disabled
                                                    className="readonly-field"
                                                    aria-label={`Secondary Skill ${i + 1
                                                        } name`}
                                                />

                                                {/* Self Competency */}
                                                <select
                                                    value={s.level || ""}
                                                    disabled
                                                    className={`competency-dropdown ${s.level?.toLowerCase() || ""
                                                        }`}
                                                    aria-label={`Secondary Skill ${i + 1
                                                        } self competency level`}
                                                >
                                                    <option value="">
                                                        {s.level || "Not Provided"}
                                                    </option>
                                                </select>

                                                {/* Manager Competency */}
                                                <select
                                                    value={s.managerLevel || ""}
                                                    onChange={e =>
                                                        handleManagerLevelChange(
                                                            i,
                                                            e.target.value,
                                                            "secondary"
                                                        )
                                                    }
                                                    className={`manager-review-dropdown ${s.managerLevel?.toLowerCase() || ""
                                                        }`}
                                                    required
                                                    aria-label={`Secondary Skill ${i + 1
                                                        } manager evaluated competency level`}
                                                    disabled={
                                                        isSubmitted ||
                                                        !isFreezeWindowOpen ||
                                                        !isManager
                                                    }
                                                >
                                                    <option value="">
                                                        Manager Review
                                                    </option>
                                                    <option value="Beginner">
                                                        Beginner
                                                    </option>
                                                    <option value="Intermediate">
                                                        Intermediate
                                                    </option>
                                                    <option value="Expert">
                                                        Expert
                                                    </option>
                                                </select>

                                                {/* Target Competency */}
                                                <select
                                                    value={s.targetLevel || ""}
                                                    onChange={e =>
                                                        handleTargetLevelChange(
                                                            i,
                                                            e.target.value,
                                                            "secondary"
                                                        )
                                                    }
                                                    className={`manager-review-dropdown ${s.targetLevel?.toLowerCase() || ""
                                                        }`}
                                                    aria-label={`Secondary Skill ${i + 1
                                                        } target competency level`}
                                                    disabled={
                                                        isSubmitted ||
                                                        !isFreezeWindowOpen ||
                                                        !isManager
                                                    }
                                                >
                                                    <option value="">
                                                        Target Level
                                                    </option>
                                                    <option value="Beginner">
                                                        Beginner
                                                    </option>
                                                    <option value="Intermediate">
                                                        Intermediate
                                                    </option>
                                                    <option value="Expert">
                                                        Expert
                                                    </option>
                                                </select>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* ================= ADDITIONAL SKILL ================= */}
                            <div className="form-row full-width">
                                <div
                                    className="form-group"
                                    style={{ flex: 1 }}
                                >
                                    <label
                                        className="label-abadi"
                                        htmlFor="empdetail-additionalSkill"
                                    >
                                        Additional Skill
                                    </label>

                                    <input
                                        id="empdetail-additionalSkill"
                                        value={employeeData.additionalSkill || ""}
                                        disabled
                                        className="readonly-field"
                                    />
                                </div>
                            </div>

                            {/* ================= SOFT SKILL ================= */}
                            <div className="form-row full-width">
                                <div
                                    className="form-group"
                                    style={{ flex: 1 }}
                                >
                                    <label
                                        className="label-abadi"
                                        htmlFor="empdetail-softSkill"
                                    >
                                        Soft Skill
                                    </label>

                                    <input
                                        id="empdetail-softSkill"
                                        value={employeeData.softSkill || ""}
                                        disabled
                                        className="readonly-field"
                                    />
                                </div>
                            </div>

                            {/* ================= CERTIFICATION ================= */}
                            <div className="form-group">
                                <label
                                    className="label-abadi"
                                    htmlFor="empdetail-certification"
                                >
                                    Last Completed Certification
                                </label>

                                <input
                                    id="empdetail-certification"
                                    value={employeeData.certification || "No"}
                                    disabled
                                    className="readonly-field"
                                />

                                {employeeData.certification === "Yes" && (
                                    <div className="nested-input">
                                        <label
                                            className="label-abadi"
                                            htmlFor="empdetail-certificationName"
                                        >
                                            Certificate Name
                                        </label>

                                        <input
                                            id="empdetail-certificationName"
                                            value={
                                                employeeData.certificationName || ""
                                            }
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ================= TRAINING RECEIVED ================= */}
                            <div className="form-group">
                                <label
                                    className="label-abadi"
                                    htmlFor="empdetail-trainingReceived"
                                >
                                    Most Recent Training
                                </label>

                                <input
                                    id="empdetail-trainingReceived"
                                    value={
                                        employeeData.trainingReceived || "No"
                                    }
                                    disabled
                                    className="readonly-field"
                                />

                                {employeeData.trainingReceived === "Yes" && (
                                    <div className="nested-input">
                                        <label
                                            className="label-abadi"
                                            htmlFor="empdetail-trainingName"
                                        >
                                            Training Name
                                        </label>

                                        <input
                                            id="empdetail-trainingName"
                                            value={
                                                employeeData.trainingName || ""
                                            }
                                            disabled
                                            className="readonly-field"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ================= TRAINING RECOMMENDATION ================= */}
                            <div className="training-container">
                                <div className="form-group training-field">
                                    <label
                                        className="label-abadi"
                                        htmlFor="empdetail-managerTraining"
                                    >
                                        Upskilling & Reskilling Recommendation
                                    </label>

                                    <input
                                        id="empdetail-managerTraining"
                                        value={
                                            managerReview?.recommendedTraining ||
                                            managerTraining ||
                                            ""
                                        }
                                        onChange={e =>
                                            setManagerTraining(e.target.value)
                                        }
                                        placeholder="Enter training or certificate"
                                        className="input-field"
                                        maxLength={255}
                                        disabled={
                                            isSubmitted ||
                                            !isFreezeWindowOpen ||
                                            !isManager
                                        }
                                    />
                                </div>

                                <div className="form-group training-field">
                                    <label
                                        className="label-abadi"
                                        htmlFor="empdetail-trainingDuration"
                                    >
                                        Duration
                                    </label>

                                    <select
                                        id="empdetail-trainingDuration"
                                        value={
                                            managerReview?.trainingDuration ||
                                            trainingDuration ||
                                            ""
                                        }
                                        onChange={e =>
                                            setTrainingDuration(e.target.value)
                                        }
                                        className="input-field"
                                        disabled={
                                            isSubmitted ||
                                            !isFreezeWindowOpen ||
                                            !isManager
                                        }
                                    >
                                        <option value="">
                                            Select Duration
                                        </option>
                                        <option value="1">1 Month</option>
                                        <option value="2">2 Months</option>
                                        <option value="3">3 Months</option>
                                        <option value="4">4 Months</option>
                                        <option value="5">5 Months</option>
                                        <option value="6">6 Months</option>
                                        <option value="7">7 Months</option>
                                        <option value="8">8 Months</option>
                                        <option value="9">9 Months</option>
                                        <option value="10">10 Months</option>
                                        <option value="11">11 Months</option>
                                        <option value="12">12 Months</option>
                                    </select>
                                </div>
                            </div>

                            {/* ================= BUTTONS ================= */}
                            <div className="form-group full-width button-row">

                                <button
                                    type="button"
                                    className="submit-btn small-btn back-btn"
                                    onClick={() => goToStep(1)}
                                >
                                    ← Back
                                </button>

                                {/* MANAGER ACTION */}
                                {isManager &&
                                    isFreezeWindowOpen &&
                                    !isSubmitted && (
                                        <div className="review-actions">
                                            <button
                                                type="button"
                                                className="submit-btn big-btn save-btn"
                                                onClick={handleSaveEmployeeSkills}
                                                disabled={
                                                    loading ||
                                                    correctionLoading
                                                }
                                            >
                                                {loading
                                                    ? "Submitting..."
                                                    : "Submit Details"}
                                            </button>
                                        </div>
                                    )}

                                {/* STATUS MESSAGE */}
                                {(!isManager ||
                                    !isFreezeWindowOpen ||
                                    isSubmitted) && (
                                        <div
                                            className="review-status-note"
                                            role="status"
                                        >
                                            {!isManager &&
                                                "Only the reporting manager can submit this review."}

                                            {isManager &&
                                                !isFreezeWindowOpen &&
                                                "Freeze window is closed. Cannot submit at this time."}

                                            {isManager &&
                                                isFreezeWindowOpen &&
                                                correctionRequested && (
                                                    <>
                                                        <span>
                                                            ↩ Corrections requested —
                                                            waiting for{" "}
                                                            {employeeData.employeeName ||
                                                                "the employee"}{" "}
                                                            to resubmit. This review
                                                            reopens once they do.
                                                        </span>

                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary-mt btn-sm review-refresh-btn"
                                                            onClick={() =>
                                                                setRefreshKey(k => k + 1)
                                                            }
                                                        >
                                                            Check now
                                                        </button>
                                                    </>
                                                )}

                                            {isManager &&
                                                isFreezeWindowOpen &&
                                                isSubmitted &&
                                                !correctionRequested &&
                                                "✓ Review already submitted."}
                                        </div>
                                    )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <CustomAlert
                show={alert.show}
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ show: false, message: "", type: "success" })}
            />
        </div>
    );
}